import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowRight,
  Heart,
  Loader2,
  MapPin,
  CheckCircle2,
  EyeOff,
  AlertCircle,
  UserCheck,
  Mail,
  Phone,
  ShieldAlert,
} from "lucide-react";
import axios from "axios";
import TeroumaLogo from "../components/ui/TeroumaLogo";
import IdentityVerification from "../components/IdentityVerification";
import OnVousConnait from "../components/reconnaissance/OnVousConnait";
import IdentiteVerifiee from "../components/reconnaissance/IdentiteVerifiee";
import AdresseRecu from "../components/reconnaissance/AdresseRecu";
import { PUBLIC_API } from "../lib/api";
import { chargerFicheProuvee, nomModifie, nomsFiges } from "../lib/ficheProuvee";
import { estManquant } from "../lib/champsManquants";
import { messageErreur } from "../lib/messageErreur";
import { useI18n } from "../i18n";
import LanguageSwitcher from "../i18n/LanguageSwitcher";

const AMOUNTS = [26, 52, 104, 156, 226, 770];
// ⚠️ Valeurs envoyées à l'API (payload `motive`) : elles restent en français.
// Seul le LIBELLÉ affiché est traduit via MOTIVE_LABELS (clés i18n).
const MOTIVES = [
  "Don libre",
  "Kiddoush",
  "Aliya Torah",
  "Tehilim",
  "Hachnassat Kalla",
  "Matanot la'evyonim",
  "Tsedaka",
];
const MOTIVE_LABELS = {
  "Don libre": "donate.motive_free",
  "Kiddoush": "donate.motive_kiddoush",
  "Aliya Torah": "donate.motive_aliya",
  "Tehilim": "donate.motive_tehilim",
  "Hachnassat Kalla": "donate.motive_hachnassat",
  "Matanot la'evyonim": "donate.motive_matanot",
  "Tsedaka": "donate.motive_tsedaka",
};

export default function PublicDonatePage() {
  const { t, lang } = useI18n();
  const { orgId } = useParams();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(false);

  const [amount, setAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState("");
  const [motive, setMotive] = useState("Don libre");
  const [anonymous, setAnonymous] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dedication, setDedication] = useState("");
  const [dedicationType, setDedicationType] = useState("honneur");
  const [showDedication, setShowDedication] = useState(false);
  const [error, setError] = useState("");
  // Don particulier (défaut) ou entreprise (Cerfa personne morale art. 238 bis).
  const [donorType, setDonorType] = useState("particulier");
  const [companyName, setCompanyName] = useState("");
  const [siren, setSiren] = useState("");
  const [civility, setCivility] = useState("");
  const [address, setAddress] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");

  // Reconnaissance du donateur (décision d'Avi, 30/09/2026), fiches anonymes
  // exclues. SANS code : « ✓ On vous connaît déjà », jamais le nom ni rien de
  // la fiche — le donateur remplit et paie tout de suite. AVEC le code
  // (« Remplir mes informations ») : fiche pré-remplie et modifiable.
  const [contactCheck, setContactCheck] = useState(null);
  const [contactChecking, setContactChecking] = useState(false);
  // Drapeau COLLANT « un contact a été examiné » — voir CampaignPublicPage.
  const [contactExamined, setContactExamined] = useState(false);
  const emailTimer = useRef(null);
  const phoneTimer = useRef(null);

  // Identity verification — the reference anti-usurpation system
  const [verificationToken, setVerificationToken] = useState("");
  const identityVerified = Boolean(verificationToken);
  // Vérification ouverte par « Remplir mes informations » (facultative).
  const [codeOuvert, setCodeOuvert] = useState(false);
  // Fiche rendue par le serveur APRÈS le code (nom, contacts, adresse, manques).
  const [ficheProuvee, setFicheProuvee] = useState(null);
  // Un don anonyme reste forcément particulier (pas de personne morale anonyme).
  const isCompany = !anonymous && donorType === "entreprise";

  const oublierVerification = () => {
    setCodeOuvert(false);
    setVerificationToken("");
    setFicheProuvee(null);
  };

  // Reconnaissance (sans jeton) : rien d'autre que « on vous connaît déjà ».
  const doRecognize = async (params) => {
    if (!orgId) return;
    try {
      const r = await axios.get(`${PUBLIC_API}/campaigns/public/recognize-donor`, {
        params: { org_id: orgId, ...params },
      });
      setContactCheck(r.data);
    } catch {
      // Une panne de la reconnaissance ne doit JAMAIS bloquer le don : les
      // champs (gardés derrière « contact d'abord ») s'ouvrent quand même.
      setContactCheck({ found: false });
    } finally {
      setContactExamined(true);
      setContactChecking(false);
    }
  };

  // Le contact qui a servi à reconnaître le donateur : c'est lui que le code
  // prouve, il reste figé tant que l'identité est vérifiée.
  const ancre = contactCheck?.match_method === "phone" ? "phone" : "email";
  const valeurAncre = ancre === "phone" ? phone : email;

  const handleEmailChange = (v) => {
    setEmail(v);
    setContactCheck(null);
    // Changer de contact annule la vérification : il faudrait la refaire.
    oublierVerification();
    if (emailTimer.current) clearTimeout(emailTimer.current);
    if (!v.trim() || !v.includes("@") || anonymous) return;
    setContactChecking(true);
    emailTimer.current = setTimeout(() => doRecognize({ email: v.trim() }), 500);
  };

  const handlePhoneChange = (v) => {
    setPhone(v);
    if (contactCheck?.found && contactCheck?.match_method === "email") return;
    setContactCheck(null);
    oublierVerification();
    if (phoneTimer.current) clearTimeout(phoneTimer.current);
    if (v.replace(/\D/g, "").length < 8 || anonymous) return;
    setContactChecking(true);
    phoneTimer.current = setTimeout(() => doRecognize({ phone: v.trim() }), 500);
  };

  const finalAmount = customAmount ? parseFloat(customAmount) : amount;
  const reduction = Math.round(finalAmount * 0.66);
  const netCost = finalAmount - reduction;

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    // On ne récupère QUE cette organisation (profil dédié), plus tout
    // l'annuaire filtré côté client : l'annuaire embarque la photo de
    // CHAQUE synagogue en base64 et grossit à chaque nouvelle inscription,
    // ce qui pouvait faire dépasser le timeout de 10s sur mobile — pile au
    // moment où quelqu'un veut faire un don.
    axios
      .get(`${PUBLIC_API}/organizations/public/${orgId}/profile`, {
        signal: controller.signal,
      })
      .then((r) => {
        setOrg(r.data || null);
      })
      .catch((err) => {
        if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
        if (err.response?.status === 404) {
          // Organisation inexistante : message "introuvable", pas "erreur".
          setOrg(null);
        } else {
          setApiError(true);
        }
      })
      .finally(() => {
        clearTimeout(timer);
        setLoading(false);
      });
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [orgId]);

  // Identité VÉRIFIÉE → la fiche revient (avec le jeton) et pré-remplit le
  // formulaire ; le donateur corrige ensuite ce qu'il veut.
  useEffect(() => {
    if (!verificationToken || !contactCheck?.found) return;
    let annule = false;
    (async () => {
      try {
        const d = await chargerFicheProuvee({
          orgId, methode: ancre, contact: valeurAncre, jeton: verificationToken,
        });
        if (annule || !d) return;
        setFicheProuvee(d);
        if (d.first_name) setFirstName(d.first_name);
        if (d.last_name) setLastName(d.last_name);
        // Un email DÉJÀ saisi n'est jamais remplacé par celui de la fiche
        // (relecture du 01/10/2026) : reconnu par un téléphone familial, on
        // garde SON email — le serveur crée alors sa propre fiche.
        setEmail((actuel) => (d.email && !actuel.trim() ? d.email : actuel));
        if (d.phone && ancre !== "phone") setPhone(d.phone);
        if (d.address) setAddress(d.address);
        if (d.postal_code) setPostalCode(d.postal_code);
        if (d.city) setCity(d.city);
        if (d.civility) setCivility(d.civility);
        if (d.company_name) setCompanyName(d.company_name);
        if (d.siren) setSiren(d.siren);
      } catch { /* pas bloquant : le donateur remplit lui-même */ }
    })();
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationToken]);

  const recommencer = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setAnonymous(false);
    setContactCheck(null);
    setContactExamined(false);
    oublierVerification();
    // Repartir VRAIMENT de zéro : effacer aussi le contexte société/adresse
    // pré-rempli, sinon le choix au paiement ouvrirait sur « Ma société »
    // avec une raison sociale d'un autre contexte.
    setDonorType("particulier");
    setCompanyName("");
    setSiren("");
    setCivility("");
    setAddress("");
    setPostalCode("");
    setCity("");
  };

  // Ce qui manque ENCORE sur la fiche (après le code) : la liste se vide au
  // fur et à mesure que le donateur complète les champs.
  const valeurDe = { first_name: firstName, last_name: lastName, email, phone, address, postal_code: postalCode, city };
  const manquantsRestants = (ficheProuvee?.missing_fields || [])
    .filter((c) => !String(valeurDe[c] ?? "").trim());
  // Après le code, l'email et le téléphone ne sont figés QUE s'ils ont servi
  // à la reconnaissance (c'est eux que le code prouve). Reconnu par
  // téléphone, l'email reste libre : un autre email = une autre fiche.
  const emailFige = identityVerified && ancre === "email";
  const telephoneFige = identityVerified && ancre === "phone";
  // Nom de la fiche figé quand le serveur ne l'y remplacerait pas (code reçu
  // par SMS) et que le don rejoint bien cette fiche (lib/ficheProuvee.js).
  const nomFige = identityVerified ? nomsFiges(ficheProuvee, email) : { first_name: false, last_name: false };
  const figes = { email: emailFige, phone: telephoneFige, ...nomFige };
  const bordure = (champ) => (estManquant(manquantsRestants, champ)
    ? "border-amber-300 bg-amber-50/40"
    : figes[champ]
      ? "border-stone-200 bg-stone-50"
      : "border-stone-200");
  // Nouveau donateur (contact inconnu de la synagogue) : l'adresse du reçu
  // fiscal lui est proposée tout de suite, facultative.
  const nouveauDonateur = !anonymous && contactExamined && contactCheck?.found === false;
  const adresseProposee = !anonymous && ((identityVerified && contactCheck?.found) || nouveauDonateur);
  const textesVerifie = {
    titre: t("donate.verified_title"),
    corps: t("donate.verified_body"),
    recommencer: t("donate.start_over"),
    nom: t("donate.missing_name"),
    adresse: t("donate.missing_address"),
    email: t("donate.missing_email"),
    telephone: t("donate.missing_phone"),
  };

  // Whether identity verification is required for this submission. A fully
  // anonymous donation with no email at all does not need verification — the
  // donor stays untraceable. As soon as an email or phone is provided we need
  // to prove they own it.
  //
  // Paiement direct (décision d'Avi, 30/09/2026) : un PARTICULIER donne sans
  // code et part tout de suite payer ; la page de paiement redemande le code
  // seulement pour une promesse ou un paiement en plusieurs fois. Un don de
  // SOCIÉTÉ garde le code : il met à jour la fiche au nom de l'entreprise.
  const hasContact = Boolean(email || phone);
  const verificationRequired = isCompany && hasContact && !(anonymous && !email);
  const canSubmit = !verificationRequired || identityVerified;

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!finalAmount || finalAmount < 1) {
      setError(t("donate.err_min_amount"));
      return;
    }
    if (isCompany) {
      if (!companyName.trim() || !address.trim() || !postalCode.trim() || !city.trim()) {
        setError(t("donate.err_company_fields"));
        return;
      }
    } else if (!anonymous && (!firstName || !lastName)) {
      // Don sans email/téléphone (permis) : les champs noms étaient encore
      // cachés par « contact d'abord » — on les ouvre au moment où l'erreur
      // les réclame, sinon le donateur pointait des champs invisibles.
      if (!contactExamined) setContactExamined(true);
      setError(t("donate.err_name_required"));
      return;
    }
    if (verificationRequired && !identityVerified) {
      setError(t("donate.err_verify_first"));
      return;
    }
    const envoyerAdresse = isCompany || (identityVerified && !anonymous)
      || (nouveauDonateur && Boolean(address.trim() || postalCode.trim() || city.trim()));
    setSubmitting(true);
    try {
      const res = await axios.post(`${PUBLIC_API}/public/donate`, {
        organization_id: orgId,
        amount: finalAmount,
        motive,
        first_name: anonymous ? undefined : firstName,
        last_name: anonymous ? undefined : lastName,
        email: email || undefined,
        phone: anonymous ? undefined : phone,
        anonymous,
        donor_type: isCompany ? "entreprise" : "particulier",
        company_name: isCompany ? companyName : undefined,
        siren: isCompany ? siren : undefined,
        civility: isCompany ? civility || undefined : undefined,
        // Adresse : celle de la société ; avec le code, l'adresse du reçu
        // vérifiée ou complétée sur sa fiche ; ou celle qu'un nouveau
        // donateur a saisie (écrite seulement sur la fiche qu'il crée).
        address: envoyerAdresse ? address : undefined,
        postal_code: envoyerAdresse ? postalCode : undefined,
        city: envoyerAdresse ? city : undefined,
        dedication:
          showDedication && dedication
            ? `${dedicationType === "honneur" ? "En l'honneur de" : "En mémoire de"} ${dedication}`
            : undefined,
        verification_token: verificationToken || undefined,
        ...(identityVerified && !anonymous
          ? { nom_modifie: nomModifie(ficheProuvee, firstName, lastName) } : {}),
      });
      if (res.data?.pay_url) {
        window.location.href = res.data.pay_url;
        return;
      }
    } catch (e) {
      setError(messageErreur(e, t("donate.err_generic")));
      // Code déjà utilisé ou expiré : on l'oublie (les champs restent
      // remplis) — le donateur peut payer sans code ou le redemander.
      const st = e?.response?.status;
      if (identityVerified && !isCompany && (st === 403 || st === 410)) {
        setVerificationToken("");
        setFicheProuvee(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center gap-3"
        style={{ background: "#F4F3EF" }}
      >
        <Loader2 className="h-7 w-7 animate-spin text-stone-400" />
        <p className="text-sm text-stone-400">{t("donate.loading")}</p>
      </div>
    );

  if (!org)
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: "#F4F3EF" }}
      >
        <div className="text-center max-w-sm">
          <div className="h-14 w-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-6 w-6 text-red-400" />
          </div>
          <p className="text-stone-700 font-medium mb-1">
            {apiError
              ? t("donate.api_error_title")
              : t("donate.not_found_title")}
          </p>
          <p className="text-sm text-stone-400 mb-5">
            {apiError
              ? t("donate.api_error_body")
              : t("donate.not_found_body")}
          </p>
          {apiError && (
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 rounded-xl text-sm font-medium text-white mb-3 block mx-auto"
              style={{ background: "#0D1B33" }}
            >
              {t("common.retry")}
            </button>
          )}
          <Link to="/" className="block text-sm underline text-stone-400">
            {t("donate.back_home")}
          </Link>
        </div>
      </div>
    );

  return (
    <div
      className="min-h-screen"
      style={{ background: "#F4F3EF", fontFamily: "'Outfit', sans-serif" }}
    >
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-stone-200">
        <div className="max-w-2xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/">
            <TeroumaLogo textColor="#0D1B33" />
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-xs text-stone-400">
              {t("donate.nav_tagline")}
            </span>
            <LanguageSwitcher />
          </div>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className="flex items-center gap-4 mb-8">
          <div
            className="h-14 w-14 rounded-2xl flex items-center justify-center text-lg font-bold text-white shrink-0"
            style={{
              background: "linear-gradient(135deg, #0D1B33 0%, #1B2A4A 100%)",
            }}
          >
            {org.name
              .split(" ")
              .map((w) => w[0])
              .join("")
              .slice(0, 2)
              .toUpperCase()}
          </div>
          <div>
            <h1 className="text-xl font-semibold text-stone-900">{org.name}</h1>
            <div className="flex items-center gap-1 text-stone-500 text-sm">
              <MapPin className="h-3.5 w-3.5" />
              {org.city}
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {/* Amount */}
          <div className="bg-white rounded-2xl p-6 border border-stone-100 shadow-sm">
            <p className="text-[11px] uppercase tracking-[0.2em] text-stone-500 mb-4">
              {t("donate.amount_title")}
            </p>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {AMOUNTS.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => {
                    setAmount(a);
                    setCustomAmount(String(a));
                  }}
                  className={`py-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                    customAmount === String(a) ||
                    (!customAmount && amount === a)
                      ? "border-stone-900 bg-stone-900 text-white"
                      : "border-stone-200 text-stone-700 hover:border-stone-400"
                  }`}
                >
                  {a} €
                </button>
              ))}
            </div>
            <div className="relative">
              <input
                type="number"
                min="1"
                value={customAmount}
                onChange={(e) => {
                  setCustomAmount(e.target.value);
                  setAmount(0);
                }}
                placeholder={t("donate.amount_custom")}
                className={`w-full px-4 py-3 rounded-xl border-2 text-sm text-right font-semibold focus:outline-none transition-all ${customAmount ? "border-stone-900" : "border-stone-200"}`}
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 text-sm">
                €
              </span>
            </div>

            {finalAmount >= 10 && (
              <div className="mt-4 flex items-center justify-between bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3">
                <div className="text-xs text-emerald-700">
                  <span className="font-semibold">
                    {t("donate.tax_reduction_title")}
                  </span>
                  <br />
                  {t("donate.tax_reduction_body", { amount: reduction })}
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-emerald-700">
                    {netCost} €
                  </p>
                  <p className="text-[10px] text-emerald-600">
                    {t("donate.real_cost")}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Motive */}
          <div className="bg-white rounded-2xl p-6 border border-stone-100 shadow-sm">
            <p className="text-[11px] uppercase tracking-[0.2em] text-stone-500 mb-3">
              {t("donate.motive_title")}
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              {MOTIVES.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMotive(m)}
                  className={`px-3 py-1.5 rounded-full text-xs transition-all ${
                    motive === m
                      ? "bg-stone-900 text-white"
                      : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                  }`}
                >
                  {t(MOTIVE_LABELS[m] || m)}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => setShowDedication((d) => !d)}
              className="flex items-center gap-2 text-xs text-stone-500 hover:text-stone-800 transition-colors mt-2"
            >
              <Heart
                className="h-3.5 w-3.5"
                style={{ color: showDedication ? "#B8962E" : undefined }}
              />
              {showDedication
                ? t("donate.dedication_hide")
                : t("donate.dedication_show")}
            </button>
            {showDedication && (
              <div className="mt-3 space-y-2">
                <div className="flex gap-2">
                  {[
                    // Valeurs "honneur"/"memoire" : logique interne, ne pas traduire.
                    { v: "honneur", l: t("donate.dedication_honor") },
                    { v: "memoire", l: t("donate.dedication_memory") },
                  ].map((d) => (
                    <button
                      key={d.v}
                      type="button"
                      onClick={() => setDedicationType(d.v)}
                      className={`flex-1 py-2 rounded-xl text-xs border-2 transition-all ${
                        dedicationType === d.v
                          ? "border-stone-900 bg-stone-900 text-white"
                          : "border-stone-200 text-stone-600"
                      }`}
                    >
                      {d.l}
                    </button>
                  ))}
                </div>
                <input
                  value={dedication}
                  onChange={(e) => setDedication(e.target.value)}
                  placeholder={t("donate.dedication_placeholder")}
                  className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                />
              </div>
            )}
          </div>

          {/* Identity */}
          <div className="bg-white rounded-2xl p-6 border border-stone-100 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[11px] uppercase tracking-[0.2em] text-stone-500">
                {t("donate.identity_title")}
              </p>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={anonymous}
                  onChange={(e) => {
                    setAnonymous(e.target.checked);
                    oublierVerification();
                  }}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-stone-200 peer-checked:bg-stone-900 rounded-full relative transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
                <span className="text-xs text-stone-500 flex items-center gap-1">
                  <EyeOff className="h-3 w-3" /> {t("donate.anonymous")}
                </span>
              </label>
            </div>

            {anonymous ? (
              <div className="space-y-3">
                <div className="bg-stone-50 rounded-xl p-4 text-sm text-stone-500 text-center">
                  {t("donate.anonymous_note")}
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">
                    {t("common.email")}{" "}
                    <span className="text-stone-400 normal-case tracking-normal">
                      {t("donate.email_optional_cerfa")}
                    </span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setVerificationToken("");
                    }}
                    placeholder={t("donate.email_placeholder")}
                    className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                  />
                  <p className="text-[11px] text-stone-400 mt-1.5">
                    {t("donate.anonymous_email_hint")}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Le type (particulier / entreprise) n'est plus demandé ici :
                    il se choisit au moment de valider le don (fenêtre dédiée). */}

                {!contactCheck?.found && (
                  <div className="mb-1 flex items-start gap-2 px-3 py-2 rounded-xl bg-stone-50 border border-stone-200">
                    <UserCheck className="h-3.5 w-3.5 text-stone-500 mt-0.5 shrink-0" />
                    <p className="text-xs text-stone-600 leading-relaxed">
                      <strong className="text-stone-800">
                        {t("donate.returning_title")}
                      </strong>{" "}
                      {t("donate.returning_hint_prefix")}{" "}
                      <strong>{t("donate.returning_hint_email")}</strong>{" "}
                      {t("donate.returning_hint_or")}{" "}
                      <strong>{t("donate.returning_hint_phone")}</strong>
                      {t("donate.returning_hint_suffix")}
                    </p>
                  </div>
                )}

                {/* Reconnu, SANS code : jamais le nom ni rien de la fiche. */}
                {contactCheck?.found && !identityVerified && (
                  <OnVousConnait
                    methode={contactCheck.match_method}
                    onRecommencer={recommencer}
                    onRemplir={!codeOuvert ? () => setCodeOuvert(true) : undefined}
                    aide={`🛡️ ${t("donate.known_help")}`}
                    // Reconnu par l'email : le don rejoint ce dossier, reçu au
                    // nom qui y figure (le nom tapé sans code n'y est pas écrit).
                    rattachement={contactCheck.match_method === "email" ? "don" : undefined}
                    textes={{
                      titre: t("donate.known_title"),
                      reconnuEmail: t("donate.recognized_email"),
                      reconnuTelephone: t("donate.recognized_phone"),
                      remplir: t("donate.fill_my_info"),
                      recommencer: t("donate.start_over"),
                      rattachementDon: t("donate.attached_to_record"),
                    }}
                  >
                    {codeOuvert && (
                      // Le code part vers le contact qui l'a fait reconnaître
                      // (ou vers le contact enregistré de sa fiche) : c'est lui
                      // qu'il prouve, jamais un autre contact du formulaire.
                      <IdentityVerification
                        organizationId={orgId}
                        email={ancre === "email" ? valeurAncre.trim() : ""}
                        phone={ancre === "phone" ? valeurAncre.trim() : ""}
                        recognized={contactCheck}
                        onVerified={(token) => setVerificationToken(token)}
                        context="donate"
                      />
                    )}
                  </OnVousConnait>
                )}

                {/* Reconnu, AVEC code : fiche pré-remplie, modifiable. */}
                {identityVerified && contactCheck?.found && (
                  <IdentiteVerifiee manquants={manquantsRestants} onRecommencer={recommencer}
                    textes={textesVerifie} />
                )}

                {/* « Contact d'abord » : les noms n'apparaissent qu'après
                    l'examen d'un email/téléphone — la reconnaissance a sa
                    chance avant d'ouvrir les champs (comme la page de fête).
                    Ensuite toujours visibles : sans code, le donateur reconnu
                    les saisit ; avec le code, ils sont pré-remplis. */}
                {contactExamined && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">
                        {t("common.first_name")} *
                      </label>
                      <input
                        value={firstName}
                        onChange={(e) => !nomFige.first_name && setFirstName(e.target.value)}
                        readOnly={nomFige.first_name}
                        autoComplete="given-name"
                        className={`w-full px-3 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10 ${bordure("first_name")}`}
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">
                        {t("common.last_name")} *
                      </label>
                      <input
                        value={lastName}
                        onChange={(e) => !nomFige.last_name && setLastName(e.target.value)}
                        readOnly={nomFige.last_name}
                        autoComplete="family-name"
                        className={`w-full px-3 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10 ${bordure("last_name")}`}
                      />
                    </div>
                    {(nomFige.first_name || nomFige.last_name) && (
                      <p className="col-span-2 -mt-1 text-[11px] leading-relaxed text-stone-500" data-testid="nom-fige">
                        {t("donate.name_locked")}
                      </p>
                    )}
                  </div>
                )}

                {/* Email — figé après le code seulement s'il a servi à la
                    reconnaissance (voir emailFige). */}
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">
                    {t("common.email")}{" "}
                    <span className="text-stone-400 normal-case tracking-normal">
                      {t("donate.email_for_cerfa")}
                    </span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => (identityVerified
                        ? (!emailFige && setEmail(e.target.value))
                        : handleEmailChange(e.target.value))}
                      readOnly={emailFige}
                      autoComplete="email"
                      className={`w-full pl-9 pr-9 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10 transition-colors ${
                        contactCheck?.found &&
                        contactCheck?.match_method === "email"
                          ? "border-emerald-300 bg-emerald-50/30"
                          : bordure("email")
                      }`}
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {contactChecking && email && (
                        <Loader2 className="h-4 w-4 animate-spin text-stone-400" />
                      )}
                      {!contactChecking &&
                        contactCheck?.found &&
                        contactCheck?.match_method === "email" && (
                          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        )}
                    </div>
                  </div>
                </div>

                {/* Téléphone — même règle. */}
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">
                    {t("common.phone")}{" "}
                    <span className="text-stone-400 normal-case tracking-normal">
                      {t("donate.phone_optional")}
                    </span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => (identityVerified
                        ? (!telephoneFige && setPhone(e.target.value))
                        : handlePhoneChange(e.target.value))}
                      readOnly={telephoneFige}
                      placeholder="+33 6 12 34 56 78"
                      autoComplete="tel"
                      className={`w-full pl-9 pr-9 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10 transition-colors ${
                        contactCheck?.found &&
                        contactCheck?.match_method === "phone"
                          ? "border-emerald-300 bg-emerald-50/30"
                          : bordure("phone")
                      }`}
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {contactChecking && phone && !email && (
                        <Loader2 className="h-4 w-4 animate-spin text-stone-400" />
                      )}
                      {!contactChecking &&
                        contactCheck?.found &&
                        contactCheck?.match_method === "phone" && (
                          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        )}
                    </div>
                  </div>
                </div>

                {/* Adresse du reçu fiscal : avec le code (pré-remplie), ou pour
                    un nouveau donateur (facultative). */}
                {adresseProposee && (
                  <AdresseRecu
                    valeurs={{ address, postalCode, city }}
                    onChange={(champ, v) => ({ address: setAddress, postalCode: setPostalCode, city: setCity })[champ](v)}
                    manquants={manquantsRestants}
                    champClasse="text-sm"
                    textes={{
                      titre: identityVerified ? t("donate.address_title") : t("donate.address_title_optional"),
                      rue: t("donate.address_street"),
                      codePostal: t("donate.address_postal"),
                      ville: t("donate.address_city"),
                      aCompleter: t("donate.to_complete"),
                    }}
                  />
                )}
              </div>
            )}
          </div>

          {/* Identity verification (anti-usurpation) */}
          {verificationRequired && (
            <IdentityVerification
              organizationId={orgId}
              email={email}
              phone={phone}
              firstName={firstName}
              recognized={contactCheck?.found ? contactCheck : null}
              onVerified={(token) => setVerificationToken(token)}
              context="donate"
            />
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || !canSubmit}
            className="w-full py-4 rounded-2xl text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            style={{ background: "#0D1B33" }}
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : !canSubmit ? (
              <ShieldAlert className="h-4 w-4" />
            ) : (
              <ArrowRight className="h-4 w-4" />
            )}
            {!canSubmit
              ? t("donate.submit_verify_first")
              : t("donate.submit_donate", {
                  amount: (finalAmount || 0).toLocaleString(
                    lang === "en" ? "en-US" : "fr-FR",
                  ),
                })}
          </button>

          <p className="text-[11px] text-stone-400 text-center">
            {t("donate.footer_legal")}
          </p>
        </form>

      </div>
    </div>
  );
}
